import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Sparkles, Upload, Trash2, Plus, FileText, Link2, Image as ImageIcon, Loader2 } from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL || '';

const EMPTY = {
  brand_name: '',
  tagline: '',
  elevator_pitch: '',
  tone_keywords: [],
  avoid_keywords: [],
  target_audience: '',
  logo_url: '',
  logo_thumb_url: '',
  links: [],
  reference_docs: [],
  sample_copy: [],
};

export default function BrandVoicePage() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newTone, setNewTone] = useState('');
  const [newAvoid, setNewAvoid] = useState('');
  const [newLink, setNewLink] = useState({ label: '', url: '' });
  const [newSample, setNewSample] = useState({ title: '', body: '' });
  const logoInputRef = useRef(null);
  const docInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/brand-voice`);
      setForm({ ...EMPTY, ...data });
    } catch {
      toast.error('Could not load Brand Voice');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/brand-voice`, form);
      setForm({ ...EMPTY, ...data });
      toast.success('Brand Voice saved — all AI generators will now use this tone');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/api/brand-voice/upload/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, logo_url: data.url, logo_thumb_url: data.url }));
      toast.success('Logo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Logo upload failed');
    } finally { setUploading(false); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/api/brand-voice/upload/doc`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, reference_docs: [...(f.reference_docs || []), { title: data.title, url: data.url, size_bytes: data.size_bytes }] }));
      toast.success('Reference doc uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Doc upload failed');
    } finally { setUploading(false); }
  };

  const addKeyword = (list, val, setter) => {
    const trimmed = (val || '').trim();
    if (!trimmed) return;
    const dedup = [...new Set([...(form[list] || []), trimmed])];
    setForm(f => ({ ...f, [list]: dedup }));
    setter('');
  };
  const removeKeyword = (list, idx) => setForm(f => ({ ...f, [list]: f[list].filter((_, i) => i !== idx) }));
  const addLink = () => {
    if (!newLink.label.trim() || !newLink.url.trim()) return toast.error('Both label and URL required');
    setForm(f => ({ ...f, links: [...(f.links || []), { ...newLink }] }));
    setNewLink({ label: '', url: '' });
  };
  const removeLink = (idx) => setForm(f => ({ ...f, links: f.links.filter((_, i) => i !== idx) }));
  const removeDoc = (idx) => setForm(f => ({ ...f, reference_docs: f.reference_docs.filter((_, i) => i !== idx) }));
  const addSample = () => {
    if (!newSample.title.trim() || !newSample.body.trim()) return toast.error('Both title and body required');
    setForm(f => ({ ...f, sample_copy: [...(f.sample_copy || []), { ...newSample }] }));
    setNewSample({ title: '', body: '' });
  };
  const removeSample = (idx) => setForm(f => ({ ...f, sample_copy: f.sample_copy.filter((_, i) => i !== idx) }));

  if (loading) {
    return (
      <div className="page-shell-wide flex items-center justify-center py-20" data-testid="brand-voice-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="page-shell-wide space-y-6" data-testid="brand-voice-page">
      <div className="flex items-end justify-between gap-3 mb-2 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400 mb-1">
            <Sparkles className="h-3 w-3" /> Marketing · Brand Voice
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Brand Voice<PageInfoTooltip pageKey="brand-voice" /></h1>
          <p className="text-sm text-gray-600 mt-1">Every AI generator on Konnect (Content Queue, AI Influencer, AI Chat, Proposals) will match this tone.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="brand-voice-save-btn">
          {saving ? 'Saving…' : 'Save Brand Voice'}
        </Button>
      </div>

      {/* ─── Identity ─────────────────────────────────────────── */}
      <Card>
        <CardHeader><CardTitle className="text-base">Brand identity</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Brand name</Label>
            <Input value={form.brand_name} onChange={e => setForm(f => ({ ...f, brand_name: e.target.value }))} placeholder="Kaharian Konnect" data-testid="brand-voice-name" />
          </div>
          <div>
            <Label>Tagline</Label>
            <Input value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} placeholder="Run your business on autopilot" data-testid="brand-voice-tagline" />
          </div>
          <div className="md:col-span-2">
            <Label>Elevator pitch</Label>
            <Textarea rows={2} value={form.elevator_pitch} onChange={e => setForm(f => ({ ...f, elevator_pitch: e.target.value }))} placeholder="One or two sentences that explain what you do and who it's for." data-testid="brand-voice-pitch" />
          </div>
          <div className="md:col-span-2">
            <Label>Target audience</Label>
            <Textarea rows={2} value={form.target_audience} onChange={e => setForm(f => ({ ...f, target_audience: e.target.value }))} placeholder="SMB owners in SEA, 30-55, tech-adjacent, budget-conscious…" data-testid="brand-voice-audience" />
          </div>
        </CardContent>
      </Card>

      {/* ─── Tone keywords ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Tone keywords <span className="text-xs text-gray-400 ml-2">what you sound like</span></CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input value={newTone} onChange={e => setNewTone(e.target.value)} placeholder="warm, direct, expert…" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyword('tone_keywords', newTone, setNewTone))} data-testid="brand-voice-tone-input" />
              <Button variant="outline" onClick={() => addKeyword('tone_keywords', newTone, setNewTone)} data-testid="brand-voice-tone-add"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(form.tone_keywords || []).map((k, i) => (
                <Badge key={i} variant="secondary" className="pl-2 pr-1 gap-1 bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid={`brand-voice-tone-${i}`}>
                  {k}
                  <button onClick={() => removeKeyword('tone_keywords', i)} className="ml-1 text-emerald-600 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                </Badge>
              ))}
              {(form.tone_keywords || []).length === 0 && <span className="text-xs text-gray-400">No tone keywords yet</span>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Avoid keywords <span className="text-xs text-gray-400 ml-2">what you never sound like</span></CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input value={newAvoid} onChange={e => setNewAvoid(e.target.value)} placeholder="no jargon, no exclamation marks…" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyword('avoid_keywords', newAvoid, setNewAvoid))} data-testid="brand-voice-avoid-input" />
              <Button variant="outline" onClick={() => addKeyword('avoid_keywords', newAvoid, setNewAvoid)} data-testid="brand-voice-avoid-add"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(form.avoid_keywords || []).map((k, i) => (
                <Badge key={i} variant="secondary" className="pl-2 pr-1 gap-1 bg-rose-50 border border-rose-200 text-rose-800" data-testid={`brand-voice-avoid-${i}`}>
                  {k}
                  <button onClick={() => removeKeyword('avoid_keywords', i)} className="ml-1 text-rose-600 hover:text-red-700"><Trash2 className="h-3 w-3" /></button>
                </Badge>
              ))}
              {(form.avoid_keywords || []).length === 0 && <span className="text-xs text-gray-400">No avoid keywords yet</span>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ─── Logo + Links ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ImageIcon className="h-4 w-4 text-emerald-600" /> Logo</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {form.logo_url ? (
              <div className="flex items-center gap-4">
                <img src={`${API}${form.logo_url}`} alt="Brand logo" className="w-24 h-24 object-contain bg-gray-50 border rounded-lg p-2" data-testid="brand-voice-logo-preview" />
                <div className="flex flex-col gap-2">
                  <Button variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploading} data-testid="brand-voice-logo-replace">
                    <Upload className="h-4 w-4 mr-1" /> Replace
                  </Button>
                  <Button variant="ghost" onClick={() => setForm(f => ({ ...f, logo_url: '', logo_thumb_url: '' }))} className="text-red-500 hover:text-red-600" data-testid="brand-voice-logo-remove">
                    <Trash2 className="h-4 w-4 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploading} className="w-full h-24 border-dashed" data-testid="brand-voice-logo-upload">
                <Upload className="h-5 w-5 mr-2" /> {uploading ? 'Uploading…' : 'Upload logo (PNG / SVG / JPG, ≤2MB)'}
              </Button>
            )}
            <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={e => uploadLogo(e.target.files?.[0])} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Link2 className="h-4 w-4 text-sky-600" /> Brand links</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-12 gap-2">
              <Input className="col-span-4" value={newLink.label} onChange={e => setNewLink({ ...newLink, label: e.target.value })} placeholder="Website" data-testid="brand-voice-link-label" />
              <Input className="col-span-6" value={newLink.url} onChange={e => setNewLink({ ...newLink, url: e.target.value })} placeholder="https://…" data-testid="brand-voice-link-url" />
              <Button variant="outline" onClick={addLink} className="col-span-2" data-testid="brand-voice-link-add"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-1">
              {(form.links || []).map((l, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm" data-testid={`brand-voice-link-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium text-gray-800 shrink-0">{l.label}:</span>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-sky-600 truncate">{l.url}</a>
                  </div>
                  <button onClick={() => removeLink(i)} className="text-red-400 hover:text-red-600 shrink-0 ml-2"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {(form.links || []).length === 0 && <div className="text-xs text-gray-400 text-center py-2">No links yet</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ─── Reference docs ───────────────────────────────────── */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4 text-violet-600" /> Reference documents <span className="text-xs text-gray-400 ml-2">brand book, style guides, whitepapers</span></CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Button variant="outline" onClick={() => docInputRef.current?.click()} disabled={uploading} className="w-full" data-testid="brand-voice-doc-upload">
            <Upload className="h-4 w-4 mr-2" /> {uploading ? 'Uploading…' : 'Upload reference doc (PDF / DOCX / TXT, ≤10MB)'}
          </Button>
          <input ref={docInputRef} type="file" accept=".pdf,.docx,.doc,.md,.txt,.rtf" className="hidden" onChange={e => uploadDoc(e.target.files?.[0])} />
          <div className="space-y-1">
            {(form.reference_docs || []).map((d, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm" data-testid={`brand-voice-doc-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-violet-500 shrink-0" />
                  <a href={`${API}${d.url}`} target="_blank" rel="noopener noreferrer" className="text-violet-700 truncate">{d.title}</a>
                  <span className="text-xs text-gray-400 shrink-0">{(d.size_bytes / 1024).toFixed(0)} KB</span>
                </div>
                <button onClick={() => removeDoc(i)} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {(form.reference_docs || []).length === 0 && <div className="text-xs text-gray-400 text-center py-2">No reference docs yet</div>}
          </div>
        </CardContent>
      </Card>

      {/* ─── Sample copy (few-shot for AI) ─────────────────────── */}
      <Card>
        <CardHeader><CardTitle className="text-base">Sample copy <span className="text-xs text-gray-400 ml-2">hand-picked examples that capture your voice — AI uses these as few-shot references</span></CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-12 gap-2 items-start">
            <Input className="col-span-3" value={newSample.title} onChange={e => setNewSample({ ...newSample, title: e.target.value })} placeholder="Sample title (e.g. LinkedIn hook)" data-testid="brand-voice-sample-title" />
            <Textarea className="col-span-7" rows={2} value={newSample.body} onChange={e => setNewSample({ ...newSample, body: e.target.value })} placeholder="Paste 2-4 sentences that sound exactly like your brand…" data-testid="brand-voice-sample-body" />
            <Button variant="outline" onClick={addSample} className="col-span-2 h-full" data-testid="brand-voice-sample-add"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </div>
          <div className="space-y-2">
            {(form.sample_copy || []).map((s, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded border" data-testid={`brand-voice-sample-${i}`}>
                <div className="flex items-start justify-between mb-1">
                  <div className="font-medium text-sm text-gray-800">{s.title}</div>
                  <button onClick={() => removeSample(i)} className="text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{s.body}</p>
              </div>
            ))}
            {(form.sample_copy || []).length === 0 && <div className="text-xs text-gray-400 text-center py-2">No samples yet — add a few to teach the AI your voice</div>}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end pb-6">
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="brand-voice-save-btn-bottom">
          {saving ? 'Saving…' : 'Save Brand Voice'}
        </Button>
      </div>
    </div>
  );
}
